const heicExtensionPattern = /\.(heic|heif)$/i;

// WebKit silently fails to allocate a canvas backing store past this area,
// leaving drawImage a no-op while toBlob still resolves — a blank JPEG. A
// 24MP iPhone photo (5712×4284) is already over it.
const maxCanvasArea = 4096 * 4096;

export function isHeicFile(file: File): boolean {
	return (
		file.type === 'image/heic' ||
		file.type === 'image/heif' ||
		heicExtensionPattern.test(file.name)
	);
}

export function heicFileNameToJpeg(name: string): string {
	return name.replace(heicExtensionPattern, '.jpg');
}

export function clampToCanvasArea(
	width: number,
	height: number
): { width: number; height: number } {
	const area = width * height;
	if (area <= maxCanvasArea) {
		return { width, height };
	}

	const scale = Math.sqrt(maxCanvasArea / area);
	return {
		width: Math.max(1, Math.floor(width * scale)),
		height: Math.max(1, Math.floor(height * scale)),
	};
}

function decodeToImage(file: File): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const url = URL.createObjectURL(file);
		const img = new Image();
		img.onload = () => {
			// The bitmap is decoded by now, so the blob is no longer needed.
			URL.revokeObjectURL(url);
			resolve(img);
		};
		img.onerror = () => {
			URL.revokeObjectURL(url);
			reject(new Error('failed to decode image'));
		};
		img.src = url;
	});
}

// Converts a HEIC/HEIF file to JPEG by decoding it through an image element
// and re-encoding via canvas. Only WebKit decodes HEIC, which is also the
// only place the files come from (iOS hands over the raw camera file when a
// file input has no image accept hint). Anything else — non-HEIC files,
// non-WebKit browsers, corrupt files — returns the original file untouched
// so the upload proceeds as before.
export async function convertHeicToJpeg(file: File): Promise<File> {
	if (!isHeicFile(file)) {
		return file;
	}

	try {
		const img = await decodeToImage(file);

		if (!img.naturalWidth || !img.naturalHeight) {
			return file;
		}

		const { width, height } = clampToCanvasArea(
			img.naturalWidth,
			img.naturalHeight
		);
		const canvas = document.createElement('canvas');
		canvas.width = width;
		canvas.height = height;

		const context = canvas.getContext('2d');
		if (!context) {
			return file;
		}
		context.drawImage(img, 0, 0, width, height);

		const blob: Blob = await new Promise((resolve, reject) =>
			canvas.toBlob(
				(b) => (b ? resolve(b) : reject(new Error('canvas.toBlob failed'))),
				'image/jpeg',
				0.9
			)
		);

		return new File([blob], heicFileNameToJpeg(file.name), {
			type: 'image/jpeg',
			lastModified: file.lastModified,
		});
	} catch {
		return file;
	}
}
